// src/components/Screens/Home.jsx

import React, { useState } from "react";
import CustomizeModal from "../Modals/CustomizeModal";
import ConfirmLogoutModal from "../Modals/ConfirmLogoutModal";
import { AnimatePresence, motion } from "framer-motion";

export default function Home({
  settings,
  updateSettings,
  onEnterStudio,
  onLogout,
  onOpenSubscription,
}) {
  const [showCustomize, setShowCustomize] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const isDark = settings.isDarkMode;

  const expectedUnits =
    typeof settings.productionOutput === "number"
      ? settings.productionOutput
      : Number(settings.productionOutput) || 0;

  // Animation variants
  const fadeUp = {
    hidden: { opacity: 0, y: 12 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
  };

  const containerStagger = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.12,
        delayChildren: 0.15,
      },
    },
  };

  return (
    <div
      className={`relative w-full h-screen overflow-hidden font-sans ${
        isDark ? "text-white" : "text-black"
      }`}
      style={{ backgroundColor: "var(--color-background)" }}
    >
      {/* Background Video */}
      <video
        autoPlay
        muted
        loop
        playsInline
        className="absolute top-0 left-0 w-full h-full object-cover z-0"
      >
        <source src="/intro.mp4" type="video/mp4" />
      </video>

      {/* Overlay */}
      <div
        className={`absolute inset-0 z-10 ${
          isDark ? "bg-black/25" : "bg-white/40"
        }`}
      />

      {/* Center */}
      <motion.div
        className="relative z-20 flex flex-col items-center justify-center h-full text-center px-4"
        initial="hidden"
        animate="visible"
        variants={containerStagger}
      >
        {/* Logo */}
        <motion.div className="mb-4" variants={fadeUp}>
          {settings.logoUrl ? (
            <img
              src={settings.logoUrl}
              alt="Studio Logo"
              className="rounded object-contain"
              style={{
                width: "33vw",
                maxWidth: "240px",
                minWidth: "120px",
              }}
              onError={(e) => (e.currentTarget.style.display = "none")}
            />
          ) : (
            <div
              className="bg-white/30 flex items-center justify-center rounded"
              style={{
                width: "33vw",
                maxWidth: "240px",
                minWidth: "120px",
                aspectRatio: "1 / 1",
              }}
            >
              Logo
            </div>
          )}
        </motion.div>

        {/* Studio Name */}
        <motion.h1
          className="text-2xl md:text-3xl font-bold leading-tight mb-1"
          style={{
            color: "var(--color-primary)",
            textShadow: "0 1px 2px rgba(0,0,0,0.7)",
          }}
          variants={fadeUp}
        >
          {settings.businessName || "Your Studio"}
        </motion.h1>

        {/* Expected Units */}
        <motion.p className="text-xs md:text-sm mb-1 opacity-90" variants={fadeUp}>
          Expected output:&nbsp;
          <strong>{expectedUnits}</strong> units / month
        </motion.p>

        {/* Website directly under expected output */}
        {settings.website && (
          <motion.a
            href={settings.website}
            target="_blank"
            rel="noreferrer"
            className="text-xs md:text-sm mb-6 underline opacity-90 hover:opacity-100"
            style={{ color: isDark ? "#fff" : "#000" }}
            variants={fadeUp}
          >
            {(() => {
              try {
                return new URL(settings.website).host;
              } catch {
                return settings.website;
              }
            })()}
          </motion.a>
        )}

        {/* Tagline */}
        <motion.p
          className="mb-6"
          style={{
            color: isDark ? "#fff" : "#000",
            textShadow: "0 1px 1px rgba(0,0,0,0.5)",
          }}
          variants={fadeUp}
        >
          Manage your ceramics, your way.
        </motion.p>

        {/* Action Buttons */}
        <motion.div
          className="flex flex-col items-center gap-4 w-full max-w-xs"
          variants={containerStagger}
        >
          <motion.button
            variants={fadeUp}
            onClick={() => onEnterStudio("studio")}
            className="w-full px-6 py-3 rounded-lg text-white font-medium transition hover:brightness-110"
            style={{
              background:
                "linear-gradient(to right, var(--color-primary), var(--color-primary))",
            }}
          >
            Enter the Studio
          </motion.button>

          <motion.button
            variants={fadeUp}
            onClick={() => onEnterStudio("plan")}
            className="w-full px-6 py-3 rounded-lg text-white font-medium transition hover:brightness-110"
            style={{
              background:
                "linear-gradient(to right, var(--color-secondary), var(--color-primary))",
            }}
          >
            Let’s Plan
          </motion.button>

          <motion.button
            variants={fadeUp}
            onClick={() => onEnterStudio("batches")}
            className="w-full px-6 py-3 rounded-lg text-white font-medium transition hover:brightness-110"
            style={{ background: "linear-gradient(to right, #6b21a8, #9333ea)" }}
          >
            View Batches
          </motion.button>

          {/* Edit Studio button */}
          <motion.button
            variants={fadeUp}
            onClick={() => setShowCustomize(true)}
            className="w-full px-6 py-3 rounded-lg text-white font-medium transition hover:brightness-110"
            style={{ background: "linear-gradient(to right, #374151, #4b5563)" }}
          >
            Edit Studio
          </motion.button>
        </motion.div>
      </motion.div>

      {/* Confirm Logout */}
      <AnimatePresence>
        {showConfirm && (
          <ConfirmLogoutModal
            onCancel={() => setShowConfirm(false)}
            onConfirm={onLogout}
          />
        )}
      </AnimatePresence>

      {/* Customize Modal */}
      {showCustomize && (
        <CustomizeModal
          settings={settings}
          updateSettings={updateSettings}
          onClose={() => setShowCustomize(false)}
          onEnterStudio={() => {
            setShowCustomize(false);
            onEnterStudio("studio");
          }}
        />
      )}
    </div>
  );
}
